// Chakra imports
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';

import TableComponent from 'components/TableComponent';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';
import { FaEye, FaRedo } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { Column } from 'react-table';
import {
  ChargeBatch,
  ChargeBatchItem,
  ChargeBatchItemStatus,
  ChargeBatchStatus,
  findChargeBatchItems,
  findChargeBatches,
  retryChargeBatch,
} from 'services/api.service';
import { useQuery, useMutation } from '@tanstack/react-query';

export const ChargeBatchStatusRender: FC<{ status: ChargeBatchStatus }> = ({ status }) => {
  if (status === 'PENDING') return <Badge colorScheme="orange">Pendente</Badge>;
  if (status === 'PROCESSING') return <Badge colorScheme="blue">Processando</Badge>;
  if (status === 'COMPLETED') return <Badge colorScheme="green">Concluído</Badge>;
  if (status === 'PARTIALLY_COMPLETED') return <Badge colorScheme="yellow">Parcial</Badge>;
  if (status === 'FAILED') return <Badge colorScheme="red">Falhou</Badge>;
  return null;
};

export const ChargeBatchItemStatusRender: FC<{ status: ChargeBatchItemStatus }> = ({ status }) => {
  if (status === 'PENDING') return <Badge colorScheme="orange">Pendente</Badge>;
  if (status === 'PROCESSING') return <Badge colorScheme="blue">Processando</Badge>;
  if (status === 'SUCCEEDED') return <Badge colorScheme="green">Sucesso</Badge>;
  if (status === 'FAILED') return <Badge colorScheme="red">Falhou</Badge>;
  if (status === 'SKIPPED_DUPLICATE') return <Badge colorScheme="gray">Duplicado</Badge>;
  return null;
};

function BatchItemsModal({
  batch,
  isOpen,
  onClose,
}: {
  batch: ChargeBatch | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data, isLoading, refetch } = useQuery(
    ['findChargeBatchItems', batch?.id, page],
    () => findChargeBatchItems(batch!.id, { limit: pageSize, page }),
    {
      enabled: isOpen && !!batch?.id,
      keepPreviousData: true,
    },
  );

  const { mutate: retry, isLoading: retrying } = useMutation(
    () => retryChargeBatch(batch!.id),
    {
      onSuccess: (res) => {
        toast({
          title: 'Reprocessamento solicitado',
          description: `${res.requeued} item(s) reenfileirado(s).`,
          status: 'success',
          duration: 5000,
          position: 'top',
          isClosable: true,
        });
        refetch();
        queryClient.invalidateQueries({ queryKey: ['findChargeBatches'] });
      },
      onError: () => {
        toast({
          title: 'Erro ao reprocessar',
          description: 'Não foi possível reprocessar as falhas.',
          status: 'error',
          duration: 5000,
          position: 'top',
          isClosable: true,
        });
      },
    },
  );

  const items: ChargeBatchItem[] = data?.registers ?? [];
  const totalPages = data?.pages ?? 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Itens do Lote</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Flex justify="center" align="center" minH="100px">
              <Spinner />
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>#</Th>
                    <Th>Item (externalId)</Th>
                    <Th>Status</Th>
                    <Th>Cobrança</Th>
                    <Th>Tentativas</Th>
                    <Th>Erro</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {items.map((item) => (
                    <Tr key={item.id}>
                      <Td>{item.index + 1}</Td>
                      <Td>{item.externalId}</Td>
                      <Td>
                        <ChargeBatchItemStatusRender status={item.status} />
                      </Td>
                      <Td>
                        {item.chargeId ? (
                          <Button
                            size="xs"
                            variant="link"
                            colorScheme="brand"
                            onClick={() => {
                              onClose();
                              navigate(`/access/charge/${item.chargeId}`);
                            }}
                          >
                            {item.chargeId}
                          </Button>
                        ) : (
                          <Text color="gray.400" fontSize="xs">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td>{item.attempts}</Td>
                      <Td>
                        <Text fontSize="xs" color="red.500">
                          {item.error?.message ?? ''}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                  {items.length === 0 && (
                    <Tr>
                      <Td colSpan={6}>
                        <Text textAlign="center" color="gray.400">
                          Nenhum item encontrado.
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
          {totalPages > 1 && (
            <Flex justify="center" align="center" mt={3} gap={2}>
              <Button size="xs" isDisabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </Button>
              <Text fontSize="xs">
                {page + 1} / {totalPages}
              </Text>
              <Button
                size="xs"
                isDisabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Próximo
              </Button>
            </Flex>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {batch && batch.failedItems > 0 && (
            <Button
              leftIcon={<FaRedo />}
              colorScheme="orange"
              size="sm"
              isLoading={retrying}
              onClick={() => retry()}
            >
              Reprocessar falhas ({batch.failedItems})
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function ChargesBatch() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBatch, setSelectedBatch] = useState<ChargeBatch | null>(null);

  const columns = useMemo(
    () =>
      [
        {
          accessor: 'createdAt',
          Header: 'Criado em',
          Cell: ({ value }: { value: string }) => {
            return DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss');
          },
        },
        {
          Header: 'Status',
          accessor: 'status',
          Cell: ({ value }: { value: ChargeBatchStatus }) => {
            return <ChargeBatchStatusRender status={value} />;
          },
        },
        {
          Header: 'Itens',
          accessor: 'totalItems',
        },
        {
          Header: 'Sucesso',
          accessor: 'succeededItems',
        },
        {
          Header: 'Falhas',
          accessor: 'failedItems',
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const actions = (row: ChargeBatch) => (
    <Flex align="center" justify="flex-start" w="100%">
      <IconButton
        aria-label="Ver itens"
        size="sm"
        variant="outline"
        icon={<FaEye />}
        onClick={() => {
          setSelectedBatch(row);
          onOpen();
        }}
      />
    </Flex>
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['findChargeBatches'],
    });
  };

  return (
    <>
      <PullToRefresh onRefresh={handleRefresh}>
        <Box>
          <SimpleGrid
            mb="20px"
            columns={{ sm: 1 }}
            spacing={{ base: '20px', xl: '20px' }}
          >
            <TableComponent
              label="Cobranças em Lote"
              columnsData={columns}
              queryFn={findChargeBatches}
              queryKey="findChargeBatches"
              actions={actions}
            />
          </SimpleGrid>
        </Box>
      </PullToRefresh>
      <BatchItemsModal
        batch={selectedBatch}
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setSelectedBatch(null);
        }}
      />
    </>
  );
}
